import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { signOut, getLessons, getInstructorByProfile, clockIn, clockOut, createTimeOffRequest, getTimeOffRequests, supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import Sidebar from '../../components/shared/Sidebar'
import InstructorOnboarding from './InstructorOnboarding'
import { Calendar, Clock, Umbrella, CloudRain, LogOut, Check, X, MapPin, User, ChevronLeft, ChevronRight } from 'lucide-react'
import { format, addDays, startOfWeek, isSameDay } from 'date-fns'
import WeatherPage from '../Shared/WeatherPage'

const NAV = [
  { id: 'schedule',  label: 'My Schedule',   icon: Calendar },
  { id: 'weather',   label: 'Weather',        icon: CloudRain },
  { id: 'timeoff',   label: 'Time Off',       icon: Umbrella },
  { id: 'clockin',   label: 'Clock In/Out',   icon: Clock },
]

const STATUS_COLORS = {
  confirmed: { bg: 'rgba(14,170,164,0.1)', border: 'rgba(14,170,164,0.3)', text: 'var(--teal)' },
  pending:   { bg: 'rgba(212,168,75,0.1)', border: 'rgba(212,168,75,0.3)', text: '#8a6810' },
  completed: { bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', text: '#166534' },
  cancelled: { bg: 'rgba(232,97,74,0.1)', border: 'rgba(232,97,74,0.3)', text: 'var(--coral)' },
}

function WeekCalendar({ lessons, campSessions, availableLessons = [], myRequests = new Set(), onRequest }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [requesting, setRequesting] = useState(null)
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const handleReq = async (lessonId) => {
    setRequesting(lessonId)
    await onRequest(lessonId)
    setRequesting(null)
  }

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div className="card-header">
        <h3 style={{ fontWeight: 600 }}>Weekly Schedule</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(d => addDays(d, -7))}><ChevronLeft size={14} /></button>
          <span style={{ fontSize: '0.875rem', fontWeight: 500, alignSelf: 'center' }}>
            {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d')}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekStart(d => addDays(d, 7))}><ChevronRight size={14} /></button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))', minWidth: 700 }}>
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, new Date())
            const dayStr = format(day, 'yyyy-MM-dd')
            const dayLessons = lessons.filter(l => l.scheduled_date === dayStr)
            const dayCamps = campSessions.filter(s => s.session_date === dayStr)
            const dayAvailable = availableLessons.filter(l => l.scheduled_date === dayStr)
            const isEmpty = dayLessons.length === 0 && dayCamps.length === 0 && dayAvailable.length === 0
            return (
              <div key={i} style={{
                padding: '0.75rem', borderLeft: i > 0 ? '1px solid rgba(11,30,61,0.06)' : 'none',
                background: isToday ? 'rgba(14,170,164,0.03)' : 'transparent',
                minHeight: 160
              }}>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {format(day, 'EEE')}
                  </div>
                  <div style={{
                    fontSize: '1.1rem', fontWeight: isToday ? 700 : 500,
                    color: isToday ? 'var(--teal)' : 'var(--text-dark)', marginTop: 2
                  }}>
                    {format(day, 'd')}
                  </div>
                </div>

                {/* Camp sessions */}
                {dayCamps.map(s => (
                  <div key={s.id} style={{
                    padding: '5px 7px', borderRadius: 6, marginBottom: 4,
                    background: 'rgba(212,168,75,0.15)', border: '1px solid rgba(212,168,75,0.4)',
                    fontSize: '0.7rem', lineHeight: 1.3,
                  }}>
                    <div style={{ fontWeight: 700, color: '#8a6810', display: 'flex', alignItems: 'center', gap: 3 }}>
                      🏕 {s.camp?.name || 'Surf Camp'}
                    </div>
                    <div style={{ color: '#8a6810', opacity: 0.8, marginTop: 1 }}>
                      {s.start_time?.slice(0,5)} – {s.end_time?.slice(0,5)}
                    </div>
                  </div>
                ))}

                {/* Regular lessons */}
                {dayLessons.map(l => {
                  const sc = STATUS_COLORS[l.status] || STATUS_COLORS.pending
                  return (
                    <div key={l.id} style={{
                      padding: '5px 7px', borderRadius: 6, marginBottom: 4,
                      background: sc.bg, border: `1px solid ${sc.border}`,
                      fontSize: '0.7rem', lineHeight: 1.3
                    }}>
                      <div style={{ fontWeight: 700, color: sc.text, textTransform: 'capitalize' }}>{l.lesson_type}</div>
                      <div style={{ color: 'rgba(11,30,61,0.55)', marginTop: 1 }}>{l.start_time?.slice(0,5)}</div>
                      <div style={{ color: 'rgba(11,30,61,0.45)', display: 'flex', alignItems: 'center', gap: 2, marginTop: 1 }}>
                        <MapPin size={8} /> {l.location}
                      </div>
                    </div>
                  )
                })}

                {/* Available / requestable lessons */}
                {dayAvailable.map(l => {
                  const requested = myRequests.has(l.id)
                  return (
                    <div key={`avail-${l.id}`} style={{
                      padding: '5px 7px', borderRadius: 6, marginBottom: 4,
                      background: requested ? 'rgba(14,170,164,0.07)' : 'rgba(11,30,61,0.03)',
                      border: `1.5px dashed ${requested ? 'var(--teal)' : 'rgba(11,30,61,0.18)'}`,
                      fontSize: '0.7rem', lineHeight: 1.3,
                    }}>
                      <div style={{ fontWeight: 700, color: requested ? 'var(--teal)' : 'var(--text-light)', textTransform: 'capitalize', marginBottom: 2 }}>
                        {l.lesson_type}
                      </div>
                      <div style={{ color: requested ? 'var(--teal)' : 'rgba(11,30,61,0.35)', marginBottom: 3 }}>
                        {l.start_time?.slice(0,5)}
                      </div>
                      {requested ? (
                        <button
                          onClick={() => handleWithdraw(l.id)}
                          style={{
                            background: 'transparent', color: 'var(--teal)', border: '1px solid rgba(14,170,164,0.4)',
                            borderRadius: 4, padding: '2px 7px', cursor: 'pointer',
                            fontSize: '0.6rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
                          }}
                        >
                          ✓ Withdraw
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReq(l.id)}
                          disabled={requesting === l.id}
                          style={{
                            background: 'var(--navy)', color: 'white', border: 'none',
                            borderRadius: 4, padding: '2px 7px', cursor: 'pointer',
                            fontSize: '0.6rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif',
                            opacity: requesting === l.id ? 0.5 : 1,
                          }}
                        >
                          {requesting === l.id ? '...' : 'Request'}
                        </button>
                      )}
                    </div>
                  )
                })}

                {isEmpty && (
                  <div style={{ textAlign: 'center', color: 'rgba(11,30,61,0.2)', fontSize: '0.7rem', marginTop: '1rem' }}>—</div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function ClockInOut({ lessons, instructorId }) {
  const [activeLog, setActiveLog] = useState(null)
  const [elapsed, setElapsed] = useState(0)
  const [selectedLesson, setSelectedLesson] = useState('')

  useEffect(() => {
    if (!activeLog) return
    const start = new Date(activeLog.clocked_in_at).getTime()
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [activeLog])

  const today = format(new Date(), 'yyyy-MM-dd')
  const todayLessons = lessons.filter(l => l.scheduled_date === today && l.status !== 'cancelled')

  const handleClockIn = async () => {
    if (!selectedLesson) return
    const { data } = await clockIn(instructorId, selectedLesson)
    setActiveLog(data); setElapsed(0)
  }

  const handleClockOut = async () => {
    if (!activeLog) return
    await clockOut(activeLog.id)
    setActiveLog(null); setElapsed(0)
  }

  const fmt = s => `${String(Math.floor(s/3600)).padStart(2,'0')}:${String(Math.floor((s%3600)/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`

  return (
    <div className="card">
      <div className="card-header"><h3 style={{ fontWeight: 600 }}>Clock In / Out</h3></div>
      <div className="card-body">
        {activeLog ? (
          <div style={{ textAlign: 'center' }}>
            <div className="pulse-ring" style={{ width: 120, height: 120, borderRadius: '50%', border: '4px solid var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', flexDirection: 'column' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--teal)' }}>{fmt(elapsed)}</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-light)', marginTop: 2 }}>elapsed</div>
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-mid)', marginBottom: '1.25rem' }}>
              Clocked in at {new Date(activeLog.clocked_in_at).toLocaleTimeString()}
            </div>
            <button className="btn btn-primary btn-full" onClick={handleClockOut} style={{ background: 'var(--coral)' }}>
              Clock Out
            </button>
          </div>
        ) : (
          <div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">Select Today's Lesson</label>
              {todayLessons.length === 0 ? (
                <p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>No lessons scheduled for today.</p>
              ) : (
                <select className="form-control" value={selectedLesson} onChange={e => setSelectedLesson(e.target.value)}>
                  <option value="">— Select lesson —</option>
                  {todayLessons.map(l => (
                    <option key={l.id} value={l.id}>
                      {l.start_time?.slice(0,5)} — {l.lesson_type} at {l.location}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <button className="btn btn-secondary btn-full" onClick={handleClockIn} disabled={!selectedLesson}>
              <Clock size={16} /> Clock In
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function TimeOffForm({ instructorId }) {
  const [form, setForm] = useState({ start_date: '', end_date: '', reason: '' })
  const [requests, setRequests] = useState([])
  const [saving, setSaving] = useState(false)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  useEffect(() => {
    if (!instructorId) return
    getTimeOffRequests(instructorId).then(({ data }) => setRequests(data || []))
  }, [instructorId])

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true)
    const { data } = await createTimeOffRequest({ instructor_id: instructorId, ...form })
    if (data) setRequests(r => [data, ...r])
    setForm({ start_date: '', end_date: '', reason: '' })
    setSaving(false)
  }

  const STATUS_ICONS = { pending: '⏳', approved: '✅', denied: '❌' }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
      <div className="card">
        <div className="card-header"><h3 style={{ fontWeight: 600 }}>Request Time Off</h3></div>
        <form onSubmit={handleSubmit} className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input className="form-control" type="date" required value={form.start_date} onChange={set('start_date')} min={format(new Date(), 'yyyy-MM-dd')} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input className="form-control" type="date" required value={form.end_date} onChange={set('end_date')} min={form.start_date || format(new Date(), 'yyyy-MM-dd')} />
          </div>
          <div className="form-group">
            <label className="form-label">Reason (optional)</label>
            <textarea className="form-control" rows={3} value={form.reason} onChange={set('reason')} placeholder="Family event, vacation, etc..." style={{ resize: 'none' }} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-header"><h3 style={{ fontWeight: 600 }}>My Requests</h3></div>
        <div style={{ overflow: 'auto', maxHeight: 400 }}>
          {requests.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-light)', fontSize: '0.875rem' }}>
              No requests submitted yet.
            </div>
          ) : requests.map(r => (
            <div key={r.id} style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid rgba(11,30,61,0.05)', display: 'flex', gap: 10 }}>
              <div style={{ fontSize: '1.1rem' }}>{STATUS_ICONS[r.status]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{r.start_date} – {r.end_date}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{r.reason || 'No reason provided'}</div>
              </div>
              <span className={`badge ${r.status === 'approved' ? 'badge-teal' : r.status === 'denied' ? 'badge-coral' : 'badge-gold'}`} style={{ textTransform: 'capitalize', alignSelf: 'center' }}>
                {r.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function InstructorApp() {
  const { user, profile } = useAuth()
  const [page, setPage] = useState('schedule')
  const [instructor, setInstructor] = useState(null)
  const [lessons, setLessons] = useState([])
  const [campSessions, setCampSessions] = useState([])
  const [availableLessons, setAvailableLessons] = useState([])
  const [myRequests, setMyRequests] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const instructorRef = useState(null) // stable ref for realtime callback
  const [schoolUserIds, setSchoolUserIds] = useState([])

  const loadAvailableLessons = async (userIds) => {
    if (!userIds || userIds.length === 0) return
    const todayStr = format(new Date(), 'yyyy-MM-dd')
    const { data: availRows } = await supabase
      .from('lessons')
      .select('*')
      .in('customer_id', userIds)
      .is('instructor_id', null)
      .neq('status', 'cancelled')
      .gte('scheduled_date', todayStr)
      .order('scheduled_date', { ascending: true })
    setAvailableLessons(availRows || [])
  }

  useEffect(() => {
    if (!user) return
    getInstructorByProfile(user.id).then(async ({ data }) => {
      setInstructor(data)
      if (data) {
        const ws = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
        const we = format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 6), 'yyyy-MM-dd')
        const schoolId = data.school_id || data.profile?.school_id

        // Fetch lessons, camps, and requests in parallel
        const [{ data: ls }, { data: campRows }, { data: reqRows }] = await Promise.all([
          getLessons({ instructor_id: data.id, week_start: ws, week_end: we }),
          supabase
            .from('surf_camp_session_instructors')
            .select(`session:surf_camp_sessions(id, session_date, start_time, end_time, camp:surf_camps(name, status))`)
            .eq('instructor_id', data.id),
          supabase.from('lesson_requests').select('lesson_id').eq('instructor_id', data.id).eq('status', 'pending'),
        ])

        setLessons(ls || [])
        setCampSessions(
          (campRows || [])
            .map(r => r.session)
            .filter(s => s && s.camp?.status === 'active')
        )
        setMyRequests(new Set((reqRows || []).map(r => r.lesson_id)))

        // Get school user IDs once, store them, then load available lessons
        if (schoolId) {
          const { data: schoolProfiles } = await supabase
            .from('profiles')
            .select('id')
            .eq('school_id', schoolId)
          const ids = (schoolProfiles || []).map(p => p.id)
          setSchoolUserIds(ids)
          await loadAvailableLessons(ids)
        }

        setLoading(false)
      } else setLoading(false)
    })
  }, [user])

  // Realtime: refresh available lessons whenever any lesson is inserted or updated
  useEffect(() => {
    if (schoolUserIds.length === 0) return
    const channel = supabase
      .channel('instructor-available-lessons')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lessons' }, () => {
        loadAvailableLessons(schoolUserIds)
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [schoolUserIds])

  const handleRequest = async (lessonId) => {
    if (!instructor) return
    await supabase.from('lesson_requests').upsert(
      { lesson_id: lessonId, instructor_id: instructor.id, status: 'pending' },
      { onConflict: 'lesson_id,instructor_id' }
    )
    setMyRequests(prev => new Set([...prev, lessonId]))
  }

  const handleWithdraw = async (lessonId) => {
    if (!instructor) return
    await supabase
      .from('lesson_requests')
      .update({ status: 'withdrawn' })
      .eq('lesson_id', lessonId)
      .eq('instructor_id', instructor.id)
    setMyRequests(prev => { const next = new Set(prev); next.delete(lessonId); return next })
  }

  const today = format(new Date(), 'yyyy-MM-dd')
  const todayCampCount = campSessions.filter(s => s.session_date === today).length
  const todayCount = lessons.filter(l => l.scheduled_date === today).length + todayCampCount
  const weekCount = lessons.length

  if (!loading && !instructor) {
    return <InstructorOnboarding onComplete={() => {
      getInstructorByProfile(user.id).then(({ data }) => setInstructor(data))
    }} />
  }

  if (!loading && instructor && !instructor.approved) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>⏳</div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', color: 'var(--text-dark)', marginBottom: 8 }}>Application Pending</h2>
          <p style={{ color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 24 }}>
            Your instructor application has been submitted. Your school admin will review it and send you a pay rate offer before approving your account.
          </p>
          <button onClick={() => signOut().then(() => window.location.href = '/')}
            style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(11,30,61,0.2)', background: 'transparent', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', color: 'var(--text-mid)' }}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <Sidebar items={NAV} currentPage={page} onNavigate={setPage} />
      <div className="main-content">
        <div className="top-bar">
        <div className="top-bar-inner">
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-dark)' }}>
              {NAV.find(n => n.id === page)?.label}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Today</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--teal)' }}>{todayCount} lessons</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>This week</div>
              <div style={{ fontSize: '0.875rem', fontWeight: 700 }}>{weekCount} lessons</div>
            </div>
          </div>
        </div>
        </div>

        <div className="page-body">
          {page === 'schedule' && (
            <div key="schedule" className="page-enter">
              <div className="stats-grid mb-6">
                {[
                  { label: "Today's Lessons", value: todayCount, sub: `${lessons.filter(l => l.scheduled_date === today && l.status === 'confirmed').length} confirmed` },
                  { label: 'This Week', value: weekCount, sub: 'Assigned lessons' },
                  { label: 'Rating', value: instructor ? `★ ${instructor.rating?.toFixed(1)}` : '—', sub: `${instructor?.total_reviews || 0} reviews` },
                  { label: 'Hourly Rate', value: instructor ? `$${instructor.hourly_rate}` : '—', sub: 'Current rate' },
                ].map((s, i) => (
                  <div key={s.label} className="stat-card fade-in-up" style={{ animationDelay: `${i * 55}ms` }}>
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value" style={{ fontSize: '1.5rem' }}>{s.value}</div>
                    <div className="stat-sub">{s.sub}</div>
                  </div>
                ))}
              </div>
              <WeekCalendar lessons={lessons} campSessions={campSessions} availableLessons={availableLessons} myRequests={myRequests} onRequest={handleRequest} />

              {/* Today's schedule */}
              <div className="card">
                <div className="card-header"><h3 style={{ fontWeight: 600 }}>Today's Schedule</h3></div>
                {(() => {
                  const todayLessons = lessons.filter(l => l.scheduled_date === today)
                  const todayCamps = campSessions.filter(s => s.session_date === today)
                  const todayAvailable = availableLessons.filter(l => l.scheduled_date === today)
                  if (todayLessons.length === 0 && todayCamps.length === 0 && todayAvailable.length === 0) {
                    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-light)' }}>No lessons today. Enjoy the waves! 🌊</div>
                  }
                  return (
                    <>
                      {/* Camp sessions first */}
                      {todayCamps.map(s => (
                        <div key={s.id} style={{
                          padding: '1rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)',
                          display: 'flex', alignItems: 'center', gap: '1rem',
                          background: 'rgba(212,168,75,0.04)',
                        }}>
                          <div style={{ width: 4, height: 44, borderRadius: 2, background: '#D4A84B', flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: '#8a6810', display: 'flex', alignItems: 'center', gap: 6 }}>
                              🏕 {s.camp?.name || 'Surf Camp'}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', display: 'flex', gap: 10, marginTop: 2 }}>
                              <span><Clock size={11} style={{ display: 'inline' }} /> {s.start_time?.slice(0,5)} – {s.end_time?.slice(0,5)}</span>
                            </div>
                          </div>
                          <span style={{
                            background: 'rgba(212,168,75,0.15)', color: '#8a6810',
                            border: '1px solid rgba(212,168,75,0.35)',
                            borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
                          }}>Camp</span>
                        </div>
                      ))}
                      {/* Regular lessons */}
                      {todayLessons.map(l => {
                        const sc = STATUS_COLORS[l.status] || STATUS_COLORS.pending
                        return (
                          <div key={l.id} style={{
                            padding: '1rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)',
                            display: 'flex', alignItems: 'center', gap: '1rem'
                          }}>
                            <div style={{ width: 4, height: 44, borderRadius: 2, background: sc.text, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{l.lesson_type} Lesson</div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', display: 'flex', gap: 10, marginTop: 2 }}>
                                <span><Clock size={11} style={{ display: 'inline' }} /> {l.start_time?.slice(0,5)} – {l.end_time?.slice(0,5)}</span>
                                <span><MapPin size={11} style={{ display: 'inline' }} /> {l.location}</span>
                                <span><User size={11} style={{ display: 'inline' }} /> {l.num_students || 1} student{l.num_students > 1 ? 's' : ''}</span>
                              </div>
                              {l.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-mid)', marginTop: 4, fontStyle: 'italic' }}>📝 {l.notes}</div>}
                            </div>
                            <span className="badge" style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, textTransform: 'capitalize' }}>{l.status}</span>
                          </div>
                        )
                      })}

                      {/* Available lessons today — can request */}
                      {todayAvailable.map(l => {
                        const requested = myRequests.has(l.id)
                        return (
                          <div key={`avail-${l.id}`} style={{
                            padding: '1rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)',
                            display: 'flex', alignItems: 'center', gap: '1rem',
                            background: requested ? 'rgba(14,170,164,0.03)' : 'transparent',
                          }}>
                            <div style={{ width: 4, height: 44, borderRadius: 2, background: requested ? 'var(--teal)' : 'rgba(11,30,61,0.12)', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, textTransform: 'capitalize', color: requested ? 'var(--teal)' : 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                {l.lesson_type} Lesson
                                <span style={{ fontSize: '0.68rem', fontWeight: 400, color: 'var(--text-light)', background: 'rgba(11,30,61,0.06)', padding: '1px 7px', borderRadius: 10 }}>Unassigned</span>
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', display: 'flex', gap: 10, marginTop: 2 }}>
                                <span><Clock size={11} style={{ display: 'inline' }} /> {l.start_time?.slice(0,5)} – {l.end_time?.slice(0,5)}</span>
                                {l.location && <span><MapPin size={11} style={{ display: 'inline' }} /> {l.location}</span>}
                                {l.num_students > 1 && <span><User size={11} style={{ display: 'inline' }} /> {l.num_students} students</span>}
                              </div>
                            </div>
                            {requested ? (
                              <button onClick={() => handleWithdraw(l.id)} style={{ background: 'transparent', color: 'var(--teal)', border: '1px solid rgba(14,170,164,0.4)', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>✓ Withdraw</button>
                            ) : (
                              <button onClick={() => handleRequest(l.id)} style={{ background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
                                Request
                              </button>
                            )}
                          </div>
                        )
                      })}
                    </>
                  )
                })()}
              </div>
              {/* Upcoming available lessons (future days) */}
              {(() => {
                const upcoming = availableLessons.filter(l => l.scheduled_date > today)
                if (upcoming.length === 0) return null
                return (
                  <div className="card" style={{ marginTop: '1.25rem' }}>
                    <div className="card-header">
                      <h3 style={{ fontWeight: 600 }}>Available to Request</h3>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>{upcoming.length} upcoming</span>
                    </div>
                    {upcoming.map(l => {
                      const requested = myRequests.has(l.id)
                      return (
                        <div key={l.id} style={{
                          padding: '0.875rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)',
                          display: 'flex', alignItems: 'center', gap: '1rem',
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '0.875rem' }}>{l.lesson_type} Lesson</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', display: 'flex', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
                              <span>📅 {l.scheduled_date}</span>
                              <span><Clock size={10} style={{ display: 'inline' }} /> {l.start_time?.slice(0,5)} – {l.end_time?.slice(0,5)}</span>
                              {l.location && <span><MapPin size={10} style={{ display: 'inline' }} /> {l.location}</span>}
                            </div>
                          </div>
                          {requested ? (
                            <button onClick={() => handleWithdraw(l.id)} style={{ background: 'transparent', color: 'var(--teal)', border: '1px solid rgba(14,170,164,0.4)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>✓ Withdraw</button>
                          ) : (
                            <button onClick={() => handleRequest(l.id)} style={{ background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
                              Request
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}

              {/* My Pending Requests */}
              {myRequests.size > 0 && (() => {
                const pending = availableLessons.filter(l => myRequests.has(l.id))
                if (pending.length === 0) return null
                return (
                  <div className="card" style={{ marginTop: '1.25rem' }}>
                    <div className="card-header">
                      <h3 style={{ fontWeight: 600 }}>My Pending Requests</h3>
                      <span style={{ fontSize: '0.72rem', color: 'var(--teal)', fontWeight: 600 }}>{pending.length} pending</span>
                    </div>
                    {pending.map(l => (
                      <div key={l.id} style={{
                        padding: '0.875rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.05)',
                        display: 'flex', alignItems: 'center', gap: '1rem',
                        background: 'rgba(14,170,164,0.02)',
                      }}>
                        <div style={{ width: 4, height: 44, borderRadius: 2, background: 'var(--teal)', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '0.875rem', color: 'var(--teal)' }}>{l.lesson_type} Lesson</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', display: 'flex', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
                            <span>📅 {l.scheduled_date}</span>
                            <span><Clock size={10} style={{ display: 'inline' }} /> {l.start_time?.slice(0,5)} – {l.end_time?.slice(0,5)}</span>
                            {l.location && <span><MapPin size={10} style={{ display: 'inline' }} /> {l.location}</span>}
                          </div>
                        </div>
                        <button onClick={() => handleWithdraw(l.id)} style={{ background: 'transparent', color: 'var(--coral)', border: '1px solid rgba(232,97,74,0.35)', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, fontFamily: 'DM Sans, sans-serif', flexShrink: 0 }}>
                          Withdraw
                        </button>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>
          )}

          {page === 'weather' && <div key="weather" className="page-enter"><WeatherPage isInstructor={true} /></div>}
          {page === 'clockin' && instructor && (
            <div key="clockin" className="page-enter" style={{ maxWidth: 400 }}>
              <ClockInOut lessons={lessons} instructorId={instructor.id} />
            </div>
          )}

          {page === 'timeoff' && instructor && (
            <div key="timeoff" className="page-enter">
              <TimeOffForm instructorId={instructor.id} />
            </div>
          )}

          {!instructor && !loading && (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-light)' }}>
              <p>No instructor profile found for your account.</p>
              <p style={{ fontSize: '0.875rem', marginTop: 8 }}>Ask your admin to set up your instructor profile.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
